'use client';

import { useDisclosure } from '@mantine/hooks';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ReleaseNotesModal } from '@/components/release-notes/release-notes-modal';
import useLocalStorage from '@/hooks/use-local-storage';
import useRollbar from '@/hooks/use-rollbar';
import { useAuth } from '@/providers/auth-provider/auth-provider';
import { RELEASE_NOTES_PREFERENCES } from '@/utils/storage-keys';
import { isVersionNewer } from '@/utils/version-parser';

import type {
  ReleaseNotesPreference,
  ReleaseNotesPreferences,
  VersionContent,
} from '@/typings/version';

export interface ReleaseNotesContextValue {
  hasNewVersions: boolean;
  preference: ReleaseNotesPreference;
  showIndicator: boolean;
  openModal: () => void;
}

const ReleaseNotesContext = createContext<ReleaseNotesContextValue | null>(null);

export function useReleaseNotes() {
  const context = useContext(ReleaseNotesContext);
  if (!context) {
    throw new Error('useReleaseNotes must be used within ReleaseNotesProvider');
  }
  return context;
}

export const ReleaseNotesProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { user } = useAuth();
  const [latestVersion, setLatestVersion] = useState<VersionContent | null>(null);
  const [otherVersions, setOtherVersions] = useState<VersionContent[]>([]);
  const [showOtherVersions, setShowOtherVersions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [preferences, setPreferences] = useLocalStorage<ReleaseNotesPreferences>({
    key: RELEASE_NOTES_PREFERENCES,
    initialValue: {
      preference: 'default',
      lastSeenVersion: null,
    },
  });

  const hasNewVersions = useMemo(() => {
    if (!latestVersion) return false;
    if (!preferences.lastSeenVersion) return true;
    return isVersionNewer(latestVersion.metadata.version, preferences.lastSeenVersion);
  }, [latestVersion, preferences.lastSeenVersion]);

  const showIndicator = useMemo(
    () => preferences.preference !== 'never' && hasNewVersions,
    [preferences.preference, hasNewVersions],
  );

  const rollbar = useRollbar();

  const autoOpenedRef = useRef(false);
  const fetchedRef = useRef(false);

  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure();

  const fetchVersions = useCallback(async () => {
    if (!user?.role) return;

    try {
      setIsLoading(true);

      const response = await fetch(`/api/versions`);

      // Session cookie may not be set yet right after login — ignore silently
      if (response.status === 401) {
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch versions');
      }

      const data = await response.json();
      setLatestVersion(data.latestVersion);
      setOtherVersions(data.otherVersions);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      rollbar.error('Error fetching versions:', err as Error);
    }
  }, [user?.role]);

  // Fetch versions on mount (only if preference is not 'never' and user is loaded)
  useEffect(() => {
    if (preferences.preference !== 'never' && !fetchedRef.current && user) {
      fetchedRef.current = true;
      fetchVersions();
    }
  }, [preferences.preference, fetchVersions, user]);

  // Auto-open modal when preference is 'immediate' and there are new versions
  useEffect(() => {
    const shouldAutoOpen = preferences.preference === 'immediate' && hasNewVersions;

    if (shouldAutoOpen && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      openModal();
    }
  }, [preferences.preference, hasNewVersions]);

  const handleMarkAsRead = useCallback(() => {
    setPreferences((prev) => ({
      ...prev,
      lastSeenVersion: latestVersion?.metadata.version || null,
    }));
  }, [latestVersion]);

  const handlePreferenceChange = useCallback((newPreference: ReleaseNotesPreference) => {
    setPreferences((prev) => ({ ...prev, preference: newPreference }));
  }, []);

  const value: ReleaseNotesContextValue = {
    hasNewVersions,
    preference: preferences.preference,
    openModal,
    showIndicator,
  };

  return (
    <ReleaseNotesContext.Provider value={value}>
      {children}
      {modalOpened && (
        <ReleaseNotesModal
          onClose={closeModal}
          latestVersion={latestVersion}
          loading={isLoading}
          otherVersions={otherVersions}
          showOtherVersions={showOtherVersions}
          preference={preferences.preference}
          onPreferenceChange={handlePreferenceChange}
          onShowOtherVersionsChange={setShowOtherVersions}
          onMarkAsRead={handleMarkAsRead}
        />
      )}
    </ReleaseNotesContext.Provider>
  );
};
